import { useState } from "react";
import { useTheme } from "../theme";
import { resetTours } from "../Utils/tours";
import { CheckIcon, MonitorIcon, MoonIcon, RotateCcwIcon, SunIcon } from "lucide-react";

/** Small drawings of each theme, so the choice is visible before it is made. */
function Preview({ kind }) {
    const light = { ground: "#f8fafc", sheet: "#ffffff", line: "rgba(15,1,52,0.1)", ink: "#0f0134", soft: "#41424c" };
    const dark = { ground: "#10100f", sheet: "#181816", line: "#2e2e2a", ink: "#f5f5f4", soft: "#a8a29e" };
    const draw = (c) => (
        <div className="h-full w-full p-2.5" style={{ background: c.ground }}>
            <div className="h-full rounded-lg p-2" style={{ background: c.sheet, boxShadow: `0 0 0 1px ${c.line}` }}>
                <div className="h-1.5 w-8 rounded-full" style={{ background: c.ink }} />
                <div className="mt-1.5 h-1 w-12 rounded-full" style={{ background: c.soft, opacity: 0.6 }} />
                <div className="mt-2.5 h-3 w-8 rounded-full" style={{ background: "#be3120" }} />
            </div>
        </div>
    );
    if (kind === "system") {
        return (
            <div className="flex h-full w-full overflow-hidden">
                <div className="h-full w-1/2 overflow-hidden">
                    <div className="h-full w-[200%]">{draw(light)}</div>
                </div>
                <div className="h-full w-1/2 overflow-hidden">
                    <div className="h-full w-[200%] -translate-x-1/2">{draw(dark)}</div>
                </div>
            </div>
        );
    }
    return draw(kind === "dark" ? dark : light);
}

const OPTIONS = [
    { value: "light", label: "Light", icon: SunIcon },
    { value: "dark", label: "Dark", icon: MoonIcon },
    { value: "system", label: "System", icon: MonitorIcon },
];

export default function Settings() {
    const [toursReset, setToursReset] = useState(false);
    const { theme, resolvedTheme, setTheme } = useTheme();

    return (
        <div className="h-[calc(100dvh-4.25rem)] w-full touch-pan-y overflow-x-hidden overflow-y-auto overscroll-y-contain bg-ground px-4 py-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-8">
            <div className="mx-auto w-full min-w-0 max-w-[960px]">
                <h1 className="font-newsreader text-3xl font-medium tracking-tight text-secondary sm:text-5xl">Settings</h1>
                <p className="mt-2 font-Mulish text-sm text-graphite sm:text-base">Choose how TryOn AI looks on this device.</p>

                <section className="mt-6 rounded-2xl bg-white p-4 shadow-[0_0_0_1px_var(--hairline)] sm:mt-8 sm:p-6" aria-labelledby="appearance-title">
                    <h2 id="appearance-title" className="font-Mulish text-lg font-semibold text-secondary">
                        Appearance
                    </h2>
                    <p className="mt-1 font-Mulish text-sm text-graphite">System follows your device and switches with it.</p>

                    <fieldset className="mt-5">
                        <legend className="sr-only">Theme</legend>
                        <div className="grid gap-4 sm:grid-cols-3">
                            {OPTIONS.map(({ value, label, icon: Icon }) => {
                                const selected = theme === value;
                                return (
                                    <label key={value} className="block cursor-pointer">
                                        <input
                                            type="radio"
                                            name="theme"
                                            value={value}
                                            checked={selected}
                                            onChange={() => setTheme(value)}
                                            className="peer sr-only"
                                        />
                                        <span
                                            className={`block rounded-2xl border-2 p-3 transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-primary-200 ${
                                                selected ? "border-primary-500" : "border-hairline hover:border-primary-200"
                                            }`}
                                        >
                                            <span className="block h-24 overflow-hidden rounded-xl shadow-[0_0_0_1px_var(--hairline)]">
                                                <Preview kind={value} />
                                            </span>
                                            <span className="mt-3 flex items-center gap-2 font-Mulish text-sm">
                                                <Icon size={18} className="text-primary-700" aria-hidden="true" />
                                                <span className={selected ? "font-semibold text-secondary" : "text-graphite"}>{label}</span>
                                            </span>
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    </fieldset>

                    <p className="mt-4 font-Mulish text-[13px] text-graphite" role="status" aria-live="polite">
                        {theme === "system"
                            ? `Following your device. It is ${resolvedTheme} right now.`
                            : `${theme === "dark" ? "Dark" : "Light"} theme.`}{" "}
                        Your choice is saved in this browser.
                    </p>
                </section>

                <section className="mt-6 rounded-2xl bg-white p-4 shadow-[0_0_0_1px_var(--hairline)] sm:p-6" aria-labelledby="tours-title">
                    <h2 id="tours-title" className="font-Mulish text-lg font-semibold text-secondary">
                        Guided tours
                    </h2>
                    <p className="mt-1 font-Mulish text-sm text-graphite">
                        Each tool shows a short tour the first time you open it. Replay them any time.
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            onClick={() => {
                                resetTours();
                                setToursReset(true);
                            }}
                            className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-white px-4 py-2 font-Mulish text-sm text-primary-700 hover:bg-primary-50"
                        >
                            <RotateCcwIcon className="h-4 w-4" aria-hidden="true" />
                            Replay tours
                        </button>
                        <p className="inline-flex items-center gap-1.5 font-Mulish text-[13px] text-graphite" role="status" aria-live="polite">
                            {toursReset ? (
                                <>
                                    <CheckIcon className="h-4 w-4 text-primary-700" aria-hidden="true" />
                                    Tours will show again the next time you open each tool.
                                </>
                            ) : null}
                        </p>
                    </div>
                </section>
            </div>
        </div>
    );
}
